import React, { useState, useRef } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  FormControl,
  MenuItem,
  Select,
  IconButton,
  Autocomplete,
  useTheme,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableFooter,
  TableCell,
  TableRow,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import {
  insurancePlanOptions,
  roomCategoryOptions,
  servicesOptions,
  errorMessages,
} from './FormConstants'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import DeleteIcon from '@mui/icons-material/Delete'
// import DifferenceIcon from '@mui/icons-material/Difference'
import VisibilityIcon from '@mui/icons-material/Visibility'

const FinancialCounselingForm = () => {
  const [showCustomPlanField, setShowCustomPlanField] = useState(false)
  const fileInputRef = useRef<any>(null)
  const [uploadedFiles, setUploadedFiles] = useState<any>([])
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  // Dynamic values for bars
  const totalAmount = 500000
  const consumedAmount = 400000
  const availableAmount = totalAmount - consumedAmount

  const consumedPercentage = (consumedAmount / totalAmount) * 100
  const availablePercentage = (availableAmount / totalAmount) * 100
  // eslint-disable-next-line
  const [servicePricing, setServicePricing] = useState([
    {
      service: 'Laparoscopy',
      standardPrice: 2000,
      fhlpPrice: 500,
      abPrice: 0,
    },
    {
      service: 'Endoscopy',
      standardPrice: 1000,
      fhlpPrice: 200,
      abPrice: 100,
    },
    {
      service: 'General Ward',
      standardPrice: 300,
      fhlpPrice: 100,
      abPrice: 0,
    },
    {
      service: 'Implants',
      standardPrice: 5000,
      fhlpPrice: 0,
      abPrice: 200,
    },
  ])

  // Validation schema
  const validationSchema = Yup.object({
    expectedAdmissionDate: Yup.date()
      .required(errorMessages.required)
      .nullable(),
    expectedDischargeDate: Yup.date()
      .required(errorMessages.required)
      .nullable(),
    services: Yup.array()
      .of(Yup.string().required())
      .min(1, errorMessages.minSelection('service', 1)),
    insurance: Yup.string().required(errorMessages.required),
    insuranceProvider: Yup.string().required(errorMessages.required),
    otherInsuranceProvider: Yup.string().when('insuranceProvider', {
      is: (insuranceProvider: any[]) =>
        Array.isArray(insuranceProvider) && insuranceProvider.includes('Other'),
      then: (schema) => schema.required(errorMessages.required),
      otherwise: (schema) => schema.nullable(),
    }),
    doctor: Yup.string()
      .required(errorMessages.required)
      .min(2, errorMessages.minLength('Doctor', 2)),
    additionalInfo: Yup.string(),
    status: Yup.string().required(errorMessages.required),
    roomCategory: Yup.string().required(errorMessages.required),
  })

  // Formik hook
  const formik = useFormik({
    initialValues: {
      expectedAdmissionDate: null,
      expectedDischargeDate: null,
      services: [],
      insurance: null,
      insuranceProvider: '',
      otherInsuranceProvider: '',
      doctor: '',
      additionalInfo: '',
      status: '',
      roomCategory: '',
    },
    validationSchema,
    onSubmit: (values) => {
      console.log('Form submitted with values:', values)
    },
  })

  const handleFileUpload = (event: any) => {
    if (event?.target?.files) {
      const file: any = [...event.target.files]
      setUploadedFiles([...file])
    }
  }

  const handleDelete = (index: number) => {
    setUploadedFiles((prevState: any[]) => {
      const updatedFiles = [...prevState]
      updatedFiles.splice(index, 1)
      return updatedFiles
    })
  }

  const handlePreview = (file: File) => {
    const fileURL = URL.createObjectURL(file)
    window.open(fileURL, '_blank')
  }

  return (
    <Box display={'flex'} flexDirection={'column'} gap={3}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography>
            <span className={classes?.label}>Name:</span>
            <span className={classes?.value}>(auto-populated)</span>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>
            <span className={classes?.label}>Gender:</span>
            <span className={classes?.value}>(auto-populated)</span>
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography>
            <span className={classes?.label}>MUHID:</span>
            <span className={classes?.value}>(auto-populated)</span>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>
            <span className={classes?.label}>Date:</span>
            <span className={classes?.value}>(auto-populated)</span>
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography>
            <span className={classes?.label}>Age:</span>
            <span className={classes?.value}>(auto-populated)</span>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>
            <span className={classes?.label}>AB ID:</span>
            <span className={classes?.value}>(auto-populated if exists)</span>
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      {/* Overview Section */}
      <Box className={classes?.overviewContainer}>
        <Typography className={classes?.title}>Overview</Typography>
        <Typography>
          <span className={classes?.label}>AB Consumption:</span>
        </Typography>

        <Box className={classes?.barContainer}>
          <Box className={`${classes?.bar} ${classes?.totalBar}`}>
            TOTAL
            <span className={classes?.barText}>
              {totalAmount.toLocaleString()}
            </span>
          </Box>
          <Box
            className={`${classes?.bar} ${classes?.consumedBar}`}
            style={{ width: `${consumedPercentage}%` }}
          >
            Consumed
            <span className={classes?.barText}>
              {consumedAmount.toLocaleString()}
            </span>
          </Box>
          <Box
            className={`${classes?.bar} ${classes?.availableBar}`}
            style={{ width: `${availablePercentage}%` }}
          >
            Available
            <span style={{ marginLeft: 'auto', marginRight: '8px' }}>
              {availableAmount.toLocaleString()}
            </span>
          </Box>
        </Box>
      </Box>
      <Divider />

      <Grid container spacing={2}>
        {/* <Grid item xs={12} sm={6}>
            <FormControl fullWidth className={classes?.formControlContainer}>
              <div>Name</div>
              <TextField
                fullWidth
                id='name'
                name='name'
                size='small'
                variant='outlined'
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth className={classes?.formControlContainer}>
              <div>Age</div>
              <TextField
                fullWidth
                id='age'
                name='age'
                size='small'
                variant='outlined'
                type='number'
                value={formik.values.age}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.age && Boolean(formik.errors.age)}
                helperText={formik.touched.age && formik.errors.age}
              />
            </FormControl>
          </Grid> */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth className={classes?.formControlContainer}>
            <div>Expected Admission Date</div>
            <TextField
              fullWidth
              id='expected-admissionDate'
              name='expectedAdmissionDate'
              type='date'
              size='small'
              variant='outlined'
              value={formik?.values?.expectedAdmissionDate}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              error={
                formik?.touched?.expectedAdmissionDate &&
                Boolean(formik?.errors?.expectedAdmissionDate)
              }
              helperText={
                formik?.touched?.expectedAdmissionDate &&
                formik?.errors?.expectedAdmissionDate
              }
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth className={classes?.formControlContainer}>
            <div>Expected Discharge Date</div>
            <TextField
              fullWidth
              id='expected-dischargeDate'
              name='expectedDischargeDate'
              type='date'
              size='small'
              variant='outlined'
              value={formik?.values?.expectedDischargeDate}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              error={
                formik?.touched?.expectedDischargeDate &&
                Boolean(formik?.errors?.expectedDischargeDate)
              }
              helperText={
                formik?.touched?.expectedDischargeDate &&
                formik?.errors?.expectedDischargeDate
              }
            />
          </FormControl>
        </Grid>

        {/* <Grid item xs={12} sm={6}>
            <FormControl fullWidth className={classes?.formControlContainer}>
              <div>Type</div>
              <TextField
                fullWidth
                id='type'
                name='type'
                size='small'
                variant='outlined'
                value={formik.values.type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.type && Boolean(formik.errors.type)}
                helperText={formik.touched.type && formik.errors.type}
              />
            </FormControl>
          </Grid> */}

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth className={classes?.formControlContainer}>
            <div>Insurance</div>
            <RadioGroup
              row
              aria-labelledby='demo-radio-buttons-group-insurance'
              name='insurance'
              value={formik?.values?.insurance}
              onChange={formik?.handleChange}
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
            {formik?.touched?.insurance && formik?.errors?.insurance && (
              <Typography variant='body2' color='error'>
                {formik?.errors?.insurance}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {formik?.values?.insurance && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth className={classes?.formControlContainer}>
              <div>Select Insurance Provider</div>
              <Select
                id='insuranceProvider-id'
                name='insuranceProvider'
                value={formik?.values?.insuranceProvider}
                onChange={(event) => {
                  formik.setFieldValue('insuranceProvider', event.target.value)
                  setShowCustomPlanField(event.target.value === 'Other')
                }}
                onBlur={formik.handleBlur}
                size='small'
                error={
                  formik?.touched?.insuranceProvider &&
                  Boolean(formik?.errors?.insuranceProvider)
                }
              >
                {insurancePlanOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {formik?.touched?.insuranceProvider &&
                formik?.errors?.insuranceProvider && (
                  <Typography variant='body2' color='error'>
                    {formik?.errors?.insuranceProvider}
                  </Typography>
                )}
            </FormControl>
          </Grid>
        )}

        {showCustomPlanField && formik?.values?.insurance && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth className={classes?.formControlContainer}>
              <div>Other Insurance Provider</div>
              <TextField
                fullWidth
                id='other-InsuranceProvider'
                name='otherInsuranceProvider'
                size='small'
                value={formik?.values?.otherInsuranceProvider}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                error={
                  formik?.touched?.otherInsuranceProvider &&
                  Boolean(formik?.errors?.otherInsuranceProvider)
                }
                helperText={
                  formik?.touched?.otherInsuranceProvider &&
                  formik?.errors?.otherInsuranceProvider
                }
              />
            </FormControl>
          </Grid>
        )}
        {/* {showCustomPlanField && (
            <Grid item xs={12} sm={1}>
              <Box
                width={'100%'}
                height={'100%'}
                display='flex'
                justifyContent='center'
                alignItems='center'
              >
                <DifferenceIcon />
              </Box>
            </Grid>
          )} */}

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth className={classes?.formControlContainer}>
            <div>Room Category</div>
            <Select
              id='roomCategory-id'
              name='roomCategory'
              value={formik?.values?.roomCategory}
              onChange={(event) => {
                formik?.setFieldValue('roomCategory', event.target.value)
                setShowCustomPlanField(event.target.value === 'Other')
              }}
              onBlur={formik?.handleBlur}
              size='small'
              error={
                formik?.touched?.roomCategory &&
                Boolean(formik?.errors?.roomCategory)
              }
            >
              {roomCategoryOptions?.map((option, index) => (
                <MenuItem key={index} value={option?.value}>
                  {option?.label}
                </MenuItem>
              ))}
            </Select>
            {formik?.touched?.roomCategory && formik?.errors?.roomCategory && (
              <Typography variant='body2' color='error'>
                {formik?.errors?.roomCategory}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {formik?.values?.roomCategory && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth className={classes?.formControlContainer}>
              <div>Services</div>
              <Autocomplete
                multiple
                size='small'
                options={servicesOptions}
                getOptionLabel={(option) => option}
                value={formik.values.services}
                onChange={(event, value) =>
                  formik.setFieldValue('services', value)
                }
                onBlur={formik.handleBlur}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    error={
                      formik.touched.services && Boolean(formik.errors.services)
                    }
                    helperText={
                      formik.touched.services && formik.errors.services
                    }
                  />
                )}
              />
            </FormControl>
          </Grid>
        )}
        {formik?.values?.roomCategory && (
          <Grid item xs={12} mt={2} mb={2}>
            <Table size='small' stickyHeader className={classes?.serviceTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell align='center'>Standard Price</TableCell>
                  <TableCell align='center'>FHLP Price</TableCell>
                  <TableCell align='center'>AB Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {servicePricing?.map((item, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell>{item?.service}</TableCell>
                      <TableCell align='center'>
                        {item?.standardPrice}
                      </TableCell>
                      <TableCell align='center'>{item?.fhlpPrice}</TableCell>
                      <TableCell align='center'>{item?.abPrice}</TableCell>
                    </TableRow>
                  )
                })}
                <TableRow>
                  <TableCell colSpan={4} rowSpan={3}></TableCell>
                </TableRow>
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell>Total</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth className={classes?.formControlContainer}>
            <div>Doctor</div>
            <TextField
              fullWidth
              id='doctor'
              name='doctor'
              size='small'
              variant='outlined'
              value={formik.values.doctor}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.doctor && Boolean(formik.errors.doctor)}
              helperText={formik.touched.doctor && formik.errors.doctor}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth className={classes?.formControlContainer}>
            <div>Additional Info</div>
            <TextField
              fullWidth
              id='additionalComments'
              name='additionalInfo'
              size='small'
              variant='outlined'
              value={formik?.values?.additionalInfo}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              error={
                formik?.touched?.additionalInfo &&
                Boolean(formik.errors.additionalInfo)
              }
              helperText={
                formik.touched.additionalInfo && formik.errors.additionalInfo
              }
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth className={classes?.formControlContainer}>
            <div>Status</div>
            <Select
              id='status'
              name='status'
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              size='small'
              error={formik.touched.status && Boolean(formik.errors.status)}
            >
              <MenuItem value='Accepted'>Accepted</MenuItem>
              <MenuItem value='Rejected'>Rejected</MenuItem>
              <MenuItem value='On Hold'>On Hold</MenuItem>
            </Select>
            {formik.touched.status && formik.errors.status && (
              <Typography variant='body2' color='error'>
                {formik.errors.status}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth className={classes?.formControlContainer}>
            <div>Policy Doc&apos;s</div>
            <Button
              variant='outlined'
              component='label'
              className={classes?.uploadLogoButton}
            >
              <FileUploadOutlinedIcon />
              <span>Upload documents if any</span>
              <input
                id='renewalDocumentUpload'
                type='file'
                required
                multiple
                onChange={handleFileUpload}
                className={classes?.UploadFile}
                ref={fileInputRef}
              />
            </Button>
          </FormControl>
        </Grid>

        {uploadedFiles?.length > 0 && (
          <div className={classes?.filesContainer}>
            {uploadedFiles?.map((item: any, index: number) => {
              return (
                <div className={classes?.uploadedFile} key={index}>
                  <div className={classes?.uploadedFileName}>
                    {item?.originalName ? item?.originalName : item?.name}
                  </div>
                  <div className={classes?.iconButton}>
                    <div>
                      <IconButton
                        className={classes?.uploadedDocumentDelete}
                        onClick={() => {
                          handlePreview(item)
                        }}
                        disabled={item?.originalName}
                        size='small'
                      >
                        <VisibilityIcon htmlColor='#183f7c' />
                      </IconButton>
                    </div>
                    <div>
                      <IconButton
                        className={classes?.uploadedDocumentDelete}
                        onClick={() => {
                          handleDelete(index)
                        }}
                        disabled={item?.originalName}
                        size='small'
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Grid>

      <Divider />
      <div className={classes?.container}>
        <div className={classes?.header}>Consent</div>
        <Typography className={classes?.content}>
          I/We have understood everything explained to me. I guarantee to adhere
          by the hospital policy/schedule of charges against my patient medical
          requirements/consumption & hence take full responsibility of clearing
          bill and balance.
        </Typography>
        <Divider className={classes?.divider} />
        <Grid container spacing={2} className={classes?.inputRow}>
          <Grid item xs={6}>
            <span className={classes?.signatureLabel}>
              Signature of Patient/Attendant:
            </span>
            ____________________________
          </Grid>
          <Grid item xs={6}>
            <span className={classes?.signatureLabel}>Relation:</span>
            ____________________________
          </Grid>
        </Grid>
        <Grid container spacing={2} className={classes?.inputRow}>
          <Grid item xs={6}>
            <span className={classes?.signatureLabel}>Name of Attendant:</span>
            ____________________________
          </Grid>
          <Grid item xs={6}>
            <span className={classes?.signatureLabel}>Phone:</span>
            ____________________________
          </Grid>
        </Grid>
        <Divider className={classes?.divider} />
        <div className={classes?.footer}>
          <Typography>
            <span className={classes?.signatureLabel}>FOE Signature:</span>
            ____________________________
          </Typography>
        </div>
      </div>
      <Grid
        xs={12}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
      >
        <Button
          className={classes?.submitBtn}
          variant='contained'
          color='primary'
          fullWidth
          disabled={formik.isSubmitting}
          onClick={formik?.submitForm}
        >
          Submit
        </Button>
      </Grid>
    </Box>
  )
}

export default FinancialCounselingForm

const styles = (theme: any) => ({
  formControlContainer: {
    display: 'flex',
    gap: '5px',
  },
  formContainer: {
    padding: '20px 20px 60px 20px',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  formField: {
    width: '100%',
    fontSize: '8px',
  },
  formLabel: {
    display: 'flex',
    fontSize: '14px',
  },
  uploadLogoButton: {
    width: '100%',
    justifyContent: 'space-between',
    border: '1.5px dashed grey',
    textTransform: 'capitalize',
  },
  inputFileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textTransform: 'capitalize',
    cursor: 'pointer',
    maxWidth: '200px',
    display: 'inline-block',
  },
  filesContainer: {
    width: '100%',
    margin: '10px',
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    overflowX: 'scroll',
    scrollBehavior: 'smooth',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#888',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f1f1',
      borderRadius: '4px',
    },
  },
  uploadedFile: {
    minWidth: '100%',
    border: '1px solid green',
    borderRadius: '5px',
    padding: '0.4rem',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    textTransform: 'capitalize',
  },
  uploadedFileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
  },
  uploadedDocumentDelete: {
    cursor: 'pointer',
    color: 'red',
    display: 'flex',
    alignItems: 'center',
    padding: 3,
  },
  btnStyles: {
    borderRadius: '25px',
    textTransform: 'capitalize',
    background: 'red',
    color: 'white',
    padding: '8px 25px',
    fontSize: '14px',
    lineHeight: 'unset !important',
    '&:hover': {
      backgroundColor: 'red',
    },
  },
  description: {
    color: 'gray',
  },
  coveragesField: {
    '& .MuiAutocomplete-popupIndicator': {
      padding: 0,
    },
    '&.css-4xbbh5-MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input':
      {
        padding: 0,
      },
  },
  selectField: {
    width: '100%',
    height: '40px !important',
    fontSize: '14px !important',
    '&  .MuiOutlinedInput-input': {
      height: '0.7rem',
    },
  },
  datePicker: {
    '&  .MuiOutlinedInput-root': {
      height: '2.5rem !important',
    },
  },
  autoCompleteContainer: {
    '&.css-l3ln04-MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input':
      {
        padding: 'unset',
      },
  },
  autoCompleteTextfield: {
    '&.MuiAutocomplete-popupIndicator': {
      padding: 0,
    },
  },
  errorText: {
    fontSize: '0.8rem',
    marginTop: '5px',
    color: 'red',
  },
  iconButton: {
    display: 'flex',
    alignItems: 'center',
  },
  intermediary: {
    margin: theme.spacing(1),
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: 4,
    backgroundColor: '#f9f9f9',
    '&:focus': {
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
  customIntermediary: {
    margin: theme.spacing(1),
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: 4,
    backgroundColor: '#f9f9f9',
    '&:focus': {
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
  asterisk: {
    color: 'red',
  },
  formTextContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  formText: {
    width: '80%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  headingText: {
    marginBottom: '20px',
  },
  renewalFilesUpload: {
    width: '30%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  UploadFile: {
    display: 'none',
  },
  fileDownload: {
    padding: 0,
  },
  saveAsDraftButton: {
    marginTop: '20px',
  },
  submitBtn: {
    backgroundColor: '#4682b4',
    textTransform: 'capitalize',
    width: 200,
  },
  label: {
    fontWeight: 500,
  },
  value: {
    color: '#555',
    marginLeft: '8px',
  },
  overviewContainer: {
    marginTop: '24px',
    padding: '10px',
    backgroundColor: '#e0f3ff',
  },
  barContainer: {
    marginTop: '16px',
  },
  bar: {
    height: '24px',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '8px',
    borderRadius: '4px',
  },
  totalBar: {
    backgroundColor: '#CBC3E3',
    width: '100%',
  },
  consumedBar: {
    backgroundColor: '#f8d7da',
    width: '40%',
  },
  availableBar: {
    backgroundColor: '#d4edda',
    width: '60%',
  },
  barText: {
    marginLeft: 'auto',
    marginRight: '8px',
  },
  title: {
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '16px',
    backgroundColor: '#4682b4',
    color: '#fff',
    padding: '3px 3px 0px 0px',
    borderRadius: '4px',
    fontSize: '17px',
  },
  serviceTable: {
    border: '1px solid grey',
  },
  container: {
    border: '1px solid #ccc',
    borderRadius: 8,
    padding: theme.spacing(2),
    margin: 'auto',
    backgroundColor: '#f9f9f9',
  },
  header: {
    backgroundColor: '#4682b4',
    color: '#fff',
    padding: '3px 3px 0px 0px',
    textAlign: 'center',
    borderRadius: '4px',
    fontSize: '17px',
    fontWeight: 'bold',
  },
  content: {
    padding: theme.spacing(2),
    fontSize: '1rem',
    textAlign: 'center',
  },
  inputRow: {
    margin: theme.spacing(2, 0),
  },
  signatureRow: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  inputField: {
    width: '100%',
    '&:not(:last-child)': {
      marginRight: theme.spacing(2),
    },
  },
  signatureLabel: {
    fontWeight: 'bold',
    fontSize: '15px',
    marginRight: theme.spacing(1),
  },
  footer: {
    marginTop: theme.spacing(4),
    display: 'flex',
    justifyContent: 'space-between',
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
})
